import { format } from "date-fns";
import {
  notificationChannelTypes,
  notificationTemplateTypes,
} from "../notificationConstant";
import { getNotificationMessageForDisplay } from "../notificationFormUtils";

const TASK_STATUS_TONES = {
  submitted: "info",
  accepted: "primary",
  cancelled: "default",
  failed: "error",
  processed: "success",
  expired: "warning",
};

export const NOTIFICATION_STATISTIC_DEFINITIONS = [
  {
    id: "userCount",
    label: "Users Queued",
    group: "user",
    paths: ["status.taskProcessStatus.userCount", "userCount"],
  },
  {
    id: "userFailedCount",
    label: "Users Failed to Queue",
    group: "user",
    paths: ["status.taskProcessStatus.userFailedCount", "userFailedCount"],
  },
  {
    id: "userSentCount",
    label: "Users Sent",
    group: "user",
    paths: ["status.notificationProcessStatus.userSentCount", "userSentCount"],
  },
  {
    id: "failedUserSentCount",
    label: "Users Failed to Send",
    group: "user",
    paths: ["status.notificationProcessStatus.failedUserSentCount", "failedUserSentCount"],
  },
  {
    id: "notificationCount",
    label: "Notifications Generated",
    group: "notification",
    paths: ["status.taskProcessStatus.notificationCount", "notificationCount"],
  },
  {
    id: "notificationSentCount",
    label: "Notifications Sent",
    group: "notification",
    paths: ["status.notificationProcessStatus.notificationSentCount", "notificationSentCount"],
  },
  {
    id: "tokenNotFoundUserCount",
    label: "Users Token Not Found",
    group: "user",
    hidden: true,
    paths: ["status.notificationProcessStatus.tokenNotFoundUserCount", "tokenNotFoundUserCount"],
  },
];

const getByPath = (source, path) =>
  String(path)
    .split(".")
    .reduce((acc, key) => (acc == null ? acc : acc[key]), source);

const readStatisticValue = (rawTask, definition) => {
  const paths = definition?.paths || [];
  for (let i = 0; i < paths.length; i += 1) {
    const value = getByPath(rawTask, paths[i]);
    if (value === undefined || value === null || value === "") continue;
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return numeric;
  }
  return undefined;
};

export function mapNotificationStatistics(rawTask = {}, { includeHidden = false } = {}) {
  const items = NOTIFICATION_STATISTIC_DEFINITIONS.filter(
    (definition) => includeHidden || !definition.hidden
  )
    .map((definition) => {
      const value = readStatisticValue(rawTask, definition);
      if (value === undefined) return null;
      return {
        id: definition.id,
        label: definition.label,
        group: definition.group,
        value,
      };
    })
    .filter(Boolean);

  return {
    user: items.filter((item) => item.group === "user"),
    notification: items.filter((item) => item.group === "notification"),
  };
}

export function mapTaskStatus(rawStatus) {
  const label = rawStatus == null || rawStatus === "" ? "" : String(rawStatus);
  if (!label) {
    return { label: "-", tone: "default", raw: rawStatus };
  }
  return {
    label,
    tone: TASK_STATUS_TONES[label.toLowerCase()] || "default",
    raw: rawStatus,
  };
}

export function getNotificationChannelLabel(channel) {
  const value =
    typeof channel === "string" ? channel : channel?.type || channel?.value || "";
  if (!value) return "-";
  return (
    notificationChannelTypes[`${value}Notification`]?.label ||
    notificationTemplateTypes[value]?.label ||
    value
  );
}

const formatUnixSeconds = (value) => {
  const seconds = Number(value);
  if (!Number.isFinite(seconds) || seconds <= 0) return "-";
  const date = new Date(seconds * 1000);
  if (Number.isNaN(date.getTime())) return "-";
  return format(date, "dd MMM yyyy 'at' hh:mm a");
};

export function mapNotificationTask(rawTask = {}) {
  const status = mapTaskStatus(rawTask?.status?.taskStatus);
  return {
    taskId: rawTask?.taskId || "",
    channel: rawTask?.channel?.type || rawTask?.channel || "",
    channelLabel: getNotificationChannelLabel(rawTask?.channel),
    timestampLabel: formatUnixSeconds(rawTask?.creationDate),
    expiryLabel: formatUnixSeconds(rawTask?.expiry),
    severity: rawTask?.severity || "",
    statusRaw: rawTask?.status?.taskStatus || "",
    statusLabel: status.label,
    statusTone: status.tone,
    statistics: mapNotificationStatistics(rawTask),
    message: getNotificationMessageForDisplay(rawTask),
    payload: rawTask,
  };
}
